"use client";

import { c as _c } from "react/compiler-runtime";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useState, useEffect } from "react";

/**
 * Sound preferences configuration stored in localStorage
 */

const STORAGE_KEY = "sound-preferences";
const DEFAULT_PREFERENCES = {
  enabled: true,
  volume: 0.5
};

/**
 * Custom hook for managing audio preferences and user interaction state.
 *
 * This hook provides:
 * - Sound preference management (enabled/disabled, volume)
 * - User interaction detection (required for audio autoplay)
 * - Persistent storage of preferences in localStorage
 * - Audio control functions
 *
 * Usage:
 * ```tsx
 * const { canPlayAudio, soundEnabled, volume, toggleSound } = useSound()
 *
 * // Use canPlayAudio to determine if audio can be played
 * // (user has interacted AND sound is enabled)
 * <audio muted={!canPlayAudio} volume={volume} />
 * ```
 */
export function useSound() {
  const $ = _c(15);
  const [preferences, setPreferences] = useLocalStorage(STORAGE_KEY, DEFAULT_PREFERENCES);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleUserInteraction = function handleUserInteraction() {
        setHasUserInteracted(true);
      };
      const events = ["click", "touchstart", "keydown"];
      events.forEach(event => {
        document.addEventListener(event, handleUserInteraction, {
          once: true,
          passive: true
        });
      });
      return () => {
        events.forEach(event_0 => {
          document.removeEventListener(event_0, handleUserInteraction);
        });
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== setPreferences) {
    t2 = function toggleSound() {
      setPreferences(_temp);
    };
    $[2] = setPreferences;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const toggleSound = t2;
  let t3;
  if ($[4] !== setPreferences) {
    t3 = function setVolume(volume) {
      const clampedVolume = Math.max(0, Math.min(1, volume));
      setPreferences(prev_0 => ({
        ...prev_0,
        volume: clampedVolume
      }));
    };
    $[4] = setPreferences;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const setVolume = t3;
  let t4;
  if ($[6] !== setPreferences) {
    t4 = function setSoundEnabled(enabled) {
      setPreferences(prev_1 => ({
        ...prev_1,
        enabled
      }));
    };
    $[6] = setPreferences;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const setSoundEnabled = t4;
  const t5 = hasUserInteracted && preferences.enabled;
  let t6;
  if ($[8] !== hasUserInteracted || $[9] !== preferences || $[10] !== setSoundEnabled || $[11] !== setVolume || $[12] !== t5 || $[13] !== toggleSound) {
    t6 = {
      preferences,
      hasUserInteracted,
      soundEnabled: preferences.enabled,
      volume: preferences.volume,
      canPlayAudio: t5,
      toggleSound,
      setVolume,
      setSoundEnabled
    };
    $[8] = hasUserInteracted;
    $[9] = preferences;
    $[10] = setSoundEnabled;
    $[11] = setVolume;
    $[12] = t5;
    $[13] = toggleSound;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp(prev) {
  return {
    ...prev,
    enabled: !prev.enabled
  };
}